/**
 * PresentContainer
 * --
 */
import React from 'react';
import PresentPresenter from './PresentPresenter';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { decrypt } from 'utils/Crypto';
import { useGetPresentData } from 'hooks/custom';
import MessageAlert, {
  calculateTotalOptionPrice,
  generateAlphanumericCode,
  HOST_URL,
  PAYMENT_STORE_ID,
  PAYMENT_PC_URL,
  jsonToQueryString,
  DOMAIN_URL,
  DEPLOY_TYPE,
  PAYMENT_URL,
  isMobile,
} from 'utils';
import { useCreateBarcodeURL } from 'hooks/BarcodeHooks';
import Formatter from 'utils/Formatter';
import { useUserStack } from 'store';
import validateCheck from 'utils/Validation';
import html2canvas from 'html2canvas';
// import { usePaymentCancelCard } from 'hooks/PaymentHooks.';
// import { useSendPresent } from 'hooks/PresentHooks';
import dayjs from 'dayjs';
import { useGetPresentCards } from 'hooks/PresentCardHooks';
import { useGetProvisions } from 'hooks/ProvisionHooks';
import Logger from 'utils/logger';
import { useCreateTempPresent } from 'hooks/TempHooks';
import calculateApplyPolicy from 'utils/productPolicyUtils';

const PRESENT_TYPE = ['PRODUCT', 'MENU', 'CASH_VOUCHER'];

const logger = new Logger('Present');

const PresentContainer = (props) => {
  /* ===== ROUTE ===== */
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const pid = searchParams.get('pid');
  const type = searchParams.get('type') ?? 'product';

  const payData = searchParams.get('pay') ?? null;

  // NOTE: 선물할 상품,메뉴,금액권 PK
  const ProductKey = React.useMemo(() => {
    const decodeIdx = decrypt(pid);
    return Number(decodeIdx);
  }, [pid]);

  /* ===== Store ===== */
  const userInfo = useUserStack();
  /* ===== Mutate ===== */
  // const { cancelCard } = usePaymentCancelCard();
  const { createBarcodeUrl } = useCreateBarcodeURL();
  // const { sendPresent } = useSendPresent();
  const { createTempPresent } = useCreateTempPresent();

  /* ===== Data ===== */
  // 선물 데이터
  const PresentData = useGetPresentData(ProductKey, type);
  // 선물 카드
  const { data: PresentCards } = useGetPresentCards(
    {
      is_show: 'Y',
    },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        const sortedData = [...data].sort((a, b) => a.order - b.order);

        return sortedData;
      },
      initialData: [],
    },
  );
  // 약관
  const { data: Provision } = useGetProvisions(
    { provision_type: 'PRESENT' },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return null;

        return data[0];
      },
      initialData: null,
    },
  );

  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  // 선물 이미지
  const cardRef = React.useRef(null);

  // 선물 상품 데이터
  const [data, setData] = React.useState(null);
  // 로딩
  const [isLoading, setIsLoading] = React.useState(true);
  // 입력값
  const [inputValue, setInputValue] = React.useState({
    name: '',
    phone: '',
    message: '',
    agree: false,
    amount: 1,
  });
  // 결제 금액
  const [paymentPrice, setPaymentPrice] = React.useState(0);
  // 선물 카드 모달
  const [isOpened, setIsOpened] = React.useState(false);
  // 바코드 데이터
  const [barcode, setBarcode] = React.useState({
    barcode: '',
    url: null,
  });

  // 결제 진행여부
  const [isProgress, setIsProgress] = React.useState(false);
  // 결제 리턴값
  const [paymentReturn, setPaymentReturn] = React.useState(null);
  // 배송비
  const [deliveryPrice, setDeliveryPrice] = React.useState(0);
  // 배송타입
  const [deliveryType, setDeliveryType] = React.useState('DELIVERY');
  // 선물 카드
  const [selectedCard, setSelectedCard] = React.useState(null);
  // 온도옵션
  const [temperatureOption, setTemperatureOption] = React.useState('HOT');

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 필수 옵션 선택 핸들러
   * --
   * @param {string} value
   * @param {number} index
   */
  const handleOptionChange = (value, index) => {
    const newOptions = [...data.selected_option];

    newOptions[index] = value;

    setData((prev) => ({
      ...prev,
      selected_option: newOptions,
    }));
  };

  /**
   * 데이터 바인딩
   * --
   * @param {string} t
   * @param {string} v
   */
  const handleChange = (t, v) => {
    setInputValue((prev) => ({
      ...prev,
      [t]: v,
    }));
  };

  /**
   * 바코드 생성
   * --
   * @returns {null | string}
   */
  const handleCreateBarcode = async () => {
    let isSuccess = null;

    // 필수옵션 선택여부 유효성 검사
    const { selected_option } = data;
    const isNull = selected_option
      ? selected_option.some((option) => !option)
      : false;
    if (isNull) {
      MessageAlert.info('필수 옵션을 모두 선택해주세요!');
      return isSuccess;
    }

    // 받는분 유효성 검사
    const { name, phone, agree } = inputValue;

    if (!name) {
      MessageAlert.info('받는분 성함을 입력해주세요!');
      return isSuccess;
    }

    if (!phone) {
      MessageAlert.info('받는분 연락처를 입력해주세요!');
      return isSuccess;
    }

    if (!validateCheck('phone', phone)) {
      MessageAlert.info('유효하지 않은 연락처 형식입니다!');
      return isSuccess;
    }

    if (!selectedCard) {
      MessageAlert.info('선물하실 카드를 선택해주세요!');
      return isSuccess;
    }

    if (!agree) {
      MessageAlert.info('결제진행에 동의해주세요!!');
      return isSuccess;
    }

    try {
      setIsLoading(true);

      // 16자리 랜덤 인증번호 생성
      const barcodeCode = generateAlphanumericCode(16);
      // 바코드 URL 생성
      const barcodeData = await createBarcodeUrl({ text: barcodeCode });

      // 바코드 버퍼 생성 실패 처리
      if (barcodeData.status !== 200 || !barcodeData.data) {
        MessageAlert.warning('네트워크를 확인해주세요!');
        return isSuccess;
      }

      setBarcode({
        barcode: barcodeCode,
        url: barcodeData?.data?.barcode,
      });

      if (isMobile()) {
        sessionStorage.setItem(
          'barcodeData',
          JSON.stringify({
            barcode: barcodeCode,
            url: barcodeData?.data?.barcode,
          }),
        );
      }

      isSuccess = barcodeCode;
    } catch (err) {
      logger.error('CreateBarcode Error!!', err);
      MessageAlert.error('에러가 발생하였습니다.');
    } finally {
      return isSuccess;
    }
  };

  /**
   * 선물하기 결제
   * --
   * 순서
   * 바코드 생성 -> 선물하기 Temp 생성 -> 결제
   */
  const handlePresent = async () => {
    try {
      // 바코드 생성
      const code = await handleCreateBarcode();

      if (!code) return setIsLoading(false);

      const confirm = window.confirm(
        `${inputValue?.phone} 연락처로 선물을 전송하시겠습니까?`,
      );

      if (!confirm) return setIsLoading(false);

      // 선물하기 Temp 데이터 생성
      const tempIdx = await handleSubmit(code);

      if (!tempIdx) return setIsLoading(false);

      const mobileCheck = isMobile();

      const paymentData = {
        sndPaymethod: '1000000000000',
        sndStoreid: PAYMENT_STORE_ID,
        sndOrdernumber: `JM_${Formatter.dateToUnix()}`,
        sndGoodname: data?.[`${type}_name`],
        sndAmount: `${paymentPrice + deliveryPrice}`,
        sndOrdername: `${userInfo?.user_name}`,
        iframeYn: 'N',
        sndCurrencytype: 'WON',
        sndShowcard: 'C',
        sndCharSet: 'utf-8',
        sndReply: `${HOST_URL}/payment/kspay/rcv`,
        sndStoreCeoName: '양재석',
        sndStorePhoneNo: '010-5138-5511',
        sndStoreAddress: '서울 강남구 도산대로11길 15 1~4층',

        // SECTION: 가상계좌, 에스크로 파라미터
        sndEscrow: '', // 에스크로 사용여부 [선택]
        sndVirDenyBank: '', // 해당은행 미표시 [선택]
        sndVirExpDt: '', // 가상계좌마감일자 (YYYYMMDD) [선택]
        sndVirExpTm: '', // 가상계좌마감일시 (HHMMSS) [선택]

        // SECTION: 계좌이체 파라미터
        sndCashReceipt: '1', // 현금영수증 발급 여부 (0: 발급 x, 1: 발급) [선택]

        // SECTION: 휴대폰결제 파라미터
        sndGoodType: '2', // 상품유형 (1: 실물, 2: 디지털)

        tempId: tempIdx,

        ...(mobileCheck
          ? {
              path: location.pathname,
              searchParams: location.search,
              // orderData: JSON.stringify({ data: data, inputValue: inputValue }),
            }
          : {}),
      };

      // sessionStorage.setItem(
      //   'orderData',
      //   JSON.stringify({
      //     data: { ...data, present_card_id: selectedCard },
      //     inputValue: inputValue,
      //   })
      // );

      let paymentURL = '';
      if (mobileCheck) {
        paymentURL = PAYMENT_URL;
      } else {
        paymentURL = PAYMENT_PC_URL;
      }

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = paymentURL;
      form.target = '_self';

      Object.entries(paymentData).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden'; // 사용자에게는 보이지 않도록 hidden으로 설정
        input.name = key; // input의 name 속성은 객체의 key
        input.value = value; // input의 value 속성은 객체의 value
        form.appendChild(input);
      });

      form.sndReply.value = `${form.sndReply.value}${jsonToQueryString(
        paymentData,
      )}`;

      document.body.appendChild(form);

      if (mobileCheck) {
        form.submit();
      } else {
        window._pay(form);
      }

      setIsProgress(true);
    } catch (err) {
      logger.error('Error!!', err);
      MessageAlert.error('에러가 발생하였습니다.');
    }
  };

  /**
   * 선물하기 생성
   * --
   */
  const handleSubmit = React.useCallback(
    async (code) => {
      try {
        const canvas = await html2canvas(cardRef.current, { useCORS: true });

        // canvas 캡쳐 이미지를 Blob 형태로 변환
        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob((blobResult) => {
            if (blobResult) {
              resolve(blobResult);
            } else {
              reject(new Error('Blob 생성 실패'));
            }
          }, 'image/jpeg'); // 또는 원하는 이미지 형식
        });

        // Blob 데이터를 File 객체로 변환
        const file = new File([blob], `${data?.[`${type}_name`]}`, {
          type: blob.type, // 'image/jpeg'
        });

        const {
          selected_option,
          product_option,
          thumbnail,
          origin_thumbnail,
          ...rest
        } = data;

        let optionPrice = 0,
          option = [];
        if (selected_option && selected_option.length) {
          const parseOption = selected_option
            .filter((option) => option)
            .map((option) => JSON.parse(option));

          option = parseOption;
          optionPrice = calculateTotalOptionPrice(parseOption);
        }

        // 선물하기 데이터 정제
        const presentData = {
          user_id: userInfo?.user_id,

          file_name: `present/${userInfo?.user_id}/${code}/${
            data?.[`${type}_name`]
          }`,
          send_type: 'lms',
          target_user_phone: inputValue?.phone,
          barcode: code,
          message: inputValue?.message ? inputValue?.message : null,
          [`${type}_id`]: data?.[`${type}_id`],
          product: JSON.stringify({
            ...rest,
            thumbnail: origin_thumbnail,
            product_option: option,
            option_price: optionPrice,
            payment_price: paymentPrice,
            num: inputValue?.amount,
            ...(type === 'menu'
              ? { temperature_option: temperatureOption }
              : {}),
          }),
          payment_info: null,
          // payment_info:
          //   typeof payment === 'object' ? JSON.stringify(payment) : payment,
          type: type?.toLocaleUpperCase(),
          provider_type: 'JM',

          expired_date: dayjs()
            .add(data?.present_information?.present_expired_date ?? 7, 'day')
            .unix(),

          file: file,

          extendable_count:
            data?.present_information?.present_postponed_deadline ?? 0,
          received_present: inputValue?.amount,
          present_type: 'COUNT',

          delivery_price: deliveryPrice,
          payment_price: paymentPrice + deliveryPrice,
          shipping_type: type === 'product' ? deliveryType : null,
          present_card_id: rest?.present_card_id
            ? rest?.present_card_id
            : (selectedCard ?? null),
        };

        // null값 필터
        const filtered = Object.entries(presentData).reduce(
          (acc, [key, value]) => {
            if (value !== null) {
              acc[key] = value;
            }
            return acc;
          },
          {},
        );

        // 선물하기 temp 데이터 생성
        const res = await createTempPresent(filtered);

        if (res.status !== 200) {
          logger.error('Temp Created Error', res);
          MessageAlert.warning(
            '선물하기에 실패하였습니다. 잠시 후 다시 시도해주세요.',
          );
          return null;
        }

        return res.data.temp_id;
      } catch (err) {
        console.error('[handleSubmit] Error!! ', err);
        MessageAlert.error('선물하기에 실패하였습니다.');
      } finally {
        setIsLoading(() => {
          setIsLoading(false);
          setIsProgress(false);
          setPaymentReturn(null);
        }, 1000);
      }
    },
    // eslint-disable-next-line
    [cardRef, barcode, data, inputValue, type, paymentPrice, userInfo],
  );

  /* ===== HOOKS ===== */
  /**
   * 잘못된 경로 예외처리
   * --
   */
  React.useEffect(() => {
    if (!pid || !type) {
      window.alert('상품을 찾을 수 없습니다!');
      window.close();
    }

    if (type && !PRESENT_TYPE.includes(type.toLocaleUpperCase())) {
      window.alert('잘못된 접근입니다!');
      window.close();
    }
  }, [pid, type]);

  /**
   * 로딩 처리
   * --
   */
  React.useEffect(() => {
    if (!PresentData) return;

    setTimeout(() => {
      setData(PresentData);
      setIsLoading(false);
    }, 1000);

    // eslint-disable-next-line
  }, [PresentData]);

  /**
   * 결제금액 계산
   * --
   */
  React.useEffect(() => {
    if (!data) return;

    const {
      price,
      selected_option,
      delivery_price,
      free_delivery_price,
      apply_grade_benefit,
      apply_policy,
    } = data;

    let optionPrice = 0;
    if (selected_option && selected_option.length && type === 'product') {
      const parseOption = selected_option
        .filter((option) => option)
        .map((option) => JSON.parse(option));

      optionPrice = calculateTotalOptionPrice(parseOption);
    }

    const productPrice = price + optionPrice;
    const amount = inputValue?.amount ?? 1;

    // 상품 선물에만 상품 상세와 동일한 할인 정책을 옵션 및 수량 기준으로 적용합니다.
    const finalDiscountPrice =
      type === 'product'
        ? calculateApplyPolicy(
            {
              price,
              option_price: optionPrice,
              num: amount,
            },
            apply_grade_benefit,
            apply_policy,
          ).final_discount_price
        : 0;
    const finalProductPrice = productPrice * amount - finalDiscountPrice;

    setPaymentPrice(finalProductPrice);

    if (
      type === 'product' &&
      (deliveryType === 'DELIVERY' || deliveryType === 'AMOUNT')
    ) {
      setDeliveryPrice(
        finalProductPrice >= free_delivery_price ? 0 : delivery_price,
      );
    }
  }, [data, inputValue?.amount, type, deliveryType]);

  /**
   * 팝업 메세지 감지
   * --
   */
  React.useEffect(() => {
    // 메시지 수신 핸들러
    const handleMessage = (event) => {
      const allowedOrigins = [
        `http://${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `http://mall.${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://mall.${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://jm-coffee.${DOMAIN_URL}${
          DEPLOY_TYPE === 'local' ? ':3000' : ''
        }`,
        `https://mall-jm-coffee.${DOMAIN_URL}${
          DEPLOY_TYPE === 'local' ? ':3000' : ''
        }`,
      ];

      // 보안 체크: 같은 origin에서 온 메시지만 허용
      if (!allowedOrigins.includes(event.origin)) {
        console.warn('허용되지 않은 origin:', event.origin);
        return;
      }

      const message = event.data;

      // 팝업에서 보낸 데이터만 처리
      // NOTE: 결제 성공
      if (message?.source === 'payment-success') {
        const parseData = JSON.parse(message?.data);
        window.closeEvent();

        setPaymentReturn({
          status: 'success',
          data: parseData,
        });
        setIsProgress(false);
      }
      // NOTE: 결제 실패
      else if (message?.source === 'payment-fail') {
        const parseData = JSON.parse(message?.data);
        window.closeEvent();

        setPaymentReturn({
          status: 'fail',
          data: parseData,
        });
      }
    };

    window.addEventListener('message', handleMessage);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line
  }, []);

  /**
   * 결제 성공 데이터 감지
   * --
   */
  React.useEffect(() => {
    if (!paymentReturn) return;

    const {
      status,
      // data
    } = paymentReturn;

    // 결제 실패 시
    if (status !== 'success') {
      MessageAlert.error('결제에 실패하였습니다!');

      setIsProgress(false);
      setIsLoading(false);
      setPaymentReturn(null);
    } else {
      setIsProgress(false);

      setTimeout(() => {
        window.alert(`${inputValue?.phone} 연락처로 문자가 전송되었습니다.`);

        if (isMobile()) {
          if (type === 'product') {
            const isNative = window.ReactNativeWebView;
            if (isNative) {
              window.ReactNativeWebView.postMessage(
                `${JSON.stringify({ type: `${type}-present-success` })}`,
              );
              return;
            }
            navigate(`/products/${encodeURIComponent(pid)}`, { replace: true });
          } else {
            const isNative = window.ReactNativeWebView;

            if (isNative) {
              window.ReactNativeWebView.postMessage(
                `${JSON.stringify({ type: `${type}-present-success` })}`,
              );
            }
          }
        } else {
          window.close();
        }
        // handleSubmit(data);
      }, 500);
    }
    // eslint-disable-next-line
  }, [paymentReturn]);

  /**
   * 결제 팝업 상태 확인
   * --
   */
  React.useEffect(() => {
    let popupCheck;

    if (isProgress) {
      // 1초마다 팝업 상태 확인
      popupCheck = setInterval(() => {
        if (window._kspay_pop_obj?.closed) {
          window.closeIframe();
          setIsProgress(false);
          clearInterval(popupCheck); // 팝업이 닫히면 타이머 종료
        } else {
          return;
        }
      }, 1000);
    }

    return () => {
      if (popupCheck) {
        clearInterval(popupCheck);
      }
    };
  }, [isProgress]);

  /**
   * 앱 결제 후 선물 생성 처리
   * --
   */
  React.useEffect(() => {
    if (!payData) return;

    const { source, data: PayData } = JSON.parse(decodeURIComponent(payData));

    // const getItems = sessionStorage.getItem('orderData');
    // const getBarcode = sessionStorage.getItem('barcodeData');

    // const parseData = JSON.parse(getItems);

    // setInputValue(parseData?.inputValue);
    // setData(parseData?.data);
    // setBarcode(JSON.parse(getBarcode));

    setTimeout(() => {
      setPaymentReturn({
        status: source === 'payment-fail' ? 'fail' : 'success',
        data: PayData,
      });
    }, 500);
  }, [payData]);

  /**
   * 선물 카드 데이터 정제
   * --
   */
  const PresentCardList = React.useMemo(() => {
    if (!PresentCards.length) return null;

    const cardCategory = PresentCards.reduce((acc, cur) => {
      const { category_name } = cur;

      const isExist = acc[category_name];

      if (isExist) {
        acc[category_name].push(cur);
      } else {
        acc[category_name] = [cur];
      }

      return acc;
    }, {});

    return { 전체: PresentCards, ...cardCategory };
  }, [PresentCards]);

  /**
   * 메뉴 온도 옵션 초기값 설정
   * --
   */
  React.useEffect(() => {
    if (PresentData && type === 'menu') {
      const { temperature_option } = PresentData;

      const temperature =
        temperature_option === 'ALL' ? 'HOT' : temperature_option;

      setTemperatureOption(temperature);
    }
  }, [PresentData, type]);

  React.useEffect(() => {
    const handlePopState = (event) => {
      // 뒤로가기 이벤트가 발생하면 특정 페이지로 이동함
      // { replace: true } 옵션을 사용하여 히스토리에서 현재 페이지를 교체
      if (type === 'product') {
        navigate(`/products/${encodeURIComponent(pid)}`, { replace: true });
      }
    };

    // popstate 이벤트 리스너 등록
    window.addEventListener('hashchange', handlePopState);

    return () => {
      window.removeEventListener('hashchange', handlePopState);
    };
  }, [navigate, type, pid]);

  /* ===== RENDER ===== */
  return (
    <>
      <PresentPresenter
        data={data}
        cards={PresentCardList}
        isLoading={isLoading}
        type={type}
        onOptionChange={handleOptionChange}
        inputValue={inputValue}
        onChange={handleChange}
        paymentPrice={paymentPrice}
        onPresent={handlePresent}
        onSubmit={handleSubmit}
        barcode={barcode}
        isOpened={isOpened}
        onClose={setIsOpened}
        cardRef={cardRef}
        deliveryPrice={deliveryPrice}
        deliveryType={deliveryType}
        setDeliveryType={setDeliveryType}
        selectedCard={selectedCard}
        setSelectedCard={setSelectedCard}
        provision={Provision}
        temperatureOption={temperatureOption}
        setTemperatureOption={setTemperatureOption}
      />
    </>
  );
};

export default PresentContainer;
